/**
 * Single source of truth for COP device coloring.
 *
 * The map pins, legend, stats sub-rows, device list, dwell/SOS/status chips,
 * and detail panel all read from this helper so analysts see the same color
 * for the same device everywhere.
 *
 * Rule:
 *   - SRP  -> red    (#f44336) — role color, regardless of device type
 *   - RP   -> orange (#ff9800) — role color, regardless of device type
 *   - Primary subjects ("students") split by device type:
 *       phone   -> green  (#4caf50)
 *       watch   -> blue   (#2196f3)
 *       vehicle -> purple (#9c27b0)
 *       drone   -> cyan   (#00bcd4)
 *   - student (generic, no device type known) -> green fallback
 *   - unknown -> gray
 *
 * The palette intentionally matches the Pathfinder map's default device-type
 * palette so analysts moving between Pathfinder and COP see the same code.
 */

export const COP_DEVICE_COLORS = {
  phone: '#4caf50',
  watch: '#2196f3',
  vehicle: '#9c27b0',
  drone: '#00bcd4',
  wardriver: '#fb8c00',
  student: '#4caf50',
  rp: '#ff9800',
  srp: '#f44336',
  unknown: '#757575',
} as const;

export type DeviceColorKey = keyof typeof COP_DEVICE_COLORS;

/** Loose shape so any device-like payload can be classified. */
export interface DeviceColorInput {
  device_type?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
}

/**
 * Classify a device-like object into a coloring key.
 *
 * SRP/RP are checked first so role color wins over device-type color.
 * After that, the device_type field selects phone/watch/vehicle/drone. We fall
 * back to "student" when we know it's a primary subject but the device
 * type isn't recognized, and "unknown" only when nothing identifies it.
 */
export function classifyDeviceColor(input: DeviceColorInput): DeviceColorKey {
  const dt = (input.device_type || '').toLowerCase();
  const et = (input.entity_type || '').toLowerCase();
  const id = (input.entity_id || '').toLowerCase();

  if (dt === 'srp' || et === 'srp' || id.includes('srp')) return 'srp';

  // Match RP as a discrete token (rp-, -rp-, rp_, etc.) so names like
  // "PurpleHaze" don't accidentally classify as RP.
  const idTokens = id.split(/[-_\s]+/);
  if (dt === 'rp' || et === 'rp' || idTokens.includes('rp')) return 'rp';

  if (dt.includes('wardriver') || dt.includes('beluga')) return 'wardriver';
  if (dt.includes('vehicle')) return 'vehicle';
  if (dt.includes('watch')) return 'watch';
  if (dt.includes('drone')) return 'drone';
  if (dt.includes('phone')) return 'phone';

  if (dt.includes('student') || et === 'student' || id.includes('student')) return 'student';

  return 'unknown';
}

/** Resolve the hex color for any device-like payload. */
export function getDeviceColor(input: DeviceColorInput): string {
  return COP_DEVICE_COLORS[classifyDeviceColor(input)];
}
